import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import { useAuth, displayUser, isTeenAccount } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { useCookieConsent, STORAGE_KEY as COOKIE_KEY } from '../components/CookieBanner'
import { useTheme, ACCENT_PRESETS } from '../context/ThemeContext'
import { useReducedMotion } from '../context/ReducedMotionContext'
import { useCoinBalance } from '../hooks/useCoinBalance'

// ─── CSS variable shim ────────────────────────────────────────────────────────
// The new Profile UI uses semantic colour tokens; map them to the project palette.
const C = {
  textPrimary:   'var(--text)',
  textSecondary: 'var(--text-muted)',
  textTertiary:  'var(--text-faint)',
  textDanger:    '#DC2626',
  textSuccess:   '#059669',
  bg:            'var(--bg)',
  surface:       '#F5F0FF',
  border:        'var(--border)',
  borderFaint:   'rgba(233,213,255,0.55)',
}

const BASE_TABS = ['Account', 'Appearance', 'Notifications', 'Privacy', 'Blocked Users']

// ─── Main page ────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, profile } = useAuth()
  const isTeen = isTeenAccount(profile)
  let TABS = [...BASE_TABS]
  if (!isTeen) TABS.splice(2, 0, 'Content')   // after Appearance
  if (!isTeen) TABS.push('Payments')
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') ?? 'Account')

  function switchTab(tab) {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <Navbar />
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '2.5rem 1rem 5rem', fontFamily: 'inherit' }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: C.textPrimary, margin: '0 0 4px' }}>Settings</h1>
          <p style={{ fontSize: 14, color: C.textSecondary, margin: 0 }}>Manage your profile and preferences</p>
        </div>

        {/* Tab nav */}
        <div role="tablist" aria-label="Settings" style={{ display: 'flex', gap: 4, marginBottom: '2rem', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
          {TABS.map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => switchTab(tab)}
              style={{
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: activeTab === tab ? 600 : 400,
                color: activeTab === tab ? C.textPrimary : C.textSecondary,
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? `2px solid var(--purple)` : '2px solid transparent',
                cursor: 'pointer',
                marginBottom: -1,
                transition: 'color 0.15s',
                fontFamily: 'inherit',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div role="tabpanel" aria-label={activeTab}>
        {activeTab === 'Account'       && <AccountSection />}
        {activeTab === 'Appearance'    && <AppearanceSection />}
        {activeTab === 'Content'       && !isTeen && <ContentSection />}
        {activeTab === 'Notifications' && <NotificationsSection />}
        {activeTab === 'Privacy'       && <PrivacySection />}
        {activeTab === 'Blocked Users' && <BlockedUsersTab />}
        {activeTab === 'Payments'      && !isTeen && <PaymentsSection />}
        </div>

      </div>
    </div>
  )
}

// ─── Account tab ──────────────────────────────────────────────────────────────

function AccountSection() {
  const { user, profile, getToken, signOut } = useAuth()
  const navigate = useNavigate()
  const currentUser = displayUser(user, profile)
  // Mirror the server gate (user.js:852): only email/password identities must
  // re-auth with their current password to change email. OAuth-only users have
  // no password — the server skips the check for them, so we hide the field.
  const hasEmailIdentity = (user?.identities ?? []).some((id) => id.provider === 'email')

  const [newUsername,     setNewUsername]     = useState('')
  const [usernameStatus,  setUsernameStatus]  = useState('idle')
  const [usernameError,   setUsernameError]   = useState('')
  const [nextChangeDate,  setNextChangeDate]  = useState(null)

  useEffect(() => {
    ;(async () => {
      const token = await getToken()
      if (!token) return
      try {
        const { data } = await axios.get('/api/user/username-cooldown', { headers: { Authorization: `Bearer ${token}` } })
        if (data.nextChangeDate) setNextChangeDate(new Date(data.nextChangeDate))
      } catch (err) {
        console.error('[SettingsPage] Failed to fetch username cooldown:', err)
      }
    })()
  }, [getToken])

  async function handleUsername(e) {
    e.preventDefault()
    setUsernameStatus('saving'); setUsernameError('')
    try {
      const token = await getToken()
      await axios.patch('/api/user/username', { username: newUsername.trim() }, { headers: { Authorization: `Bearer ${token}` } })
      // Refresh the session so auth metadata reflects the new username everywhere
      await supabase.auth.refreshSession()
      setUsernameStatus('saved'); setNewUsername('')
      setTimeout(() => setUsernameStatus('idle'), 3000)
    } catch (err) {
      setUsernameError(err.response?.data?.error ?? 'Something went wrong.')
      setUsernameStatus('idle')
    }
  }

  const [pwForm,    setPwForm]    = useState({ current: '', next: '', confirm: '' })
  const [pwStatus,  setPwStatus]  = useState('idle')
  const [pwError,   setPwError]   = useState('')

  const [newEmail,     setNewEmail]     = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [emailStatus,  setEmailStatus]  = useState('idle')
  const [emailError,   setEmailError]   = useState('')

  async function handlePassword(e) {
    e.preventDefault()
    if (pwForm.next !== pwForm.confirm) { setPwError("Passwords don't match."); return }
    if (pwForm.next.length < 8)         { setPwError('Must be at least 8 characters.'); return }
    setPwStatus('saving'); setPwError('')
    try {
      const token = await getToken()
      const res = await axios.post('/api/user/change-password',
        { currentPassword: pwForm.current, newPassword: pwForm.next },
        { headers: { Authorization: `Bearer ${token}` } })
      setPwForm({ current: '', next: '', confirm: '' })
      const reason = res.data?.warning === 'session_revocation_failed'
        ? 'session_revocation_failed'
        : 'password_changed'
      await signOut()
      navigate(`/login?reason=${reason}`, { replace: true })
    } catch (err) {
      setPwError(err.response?.data?.error ?? 'Could not update password.')
      setPwStatus('idle')
    }
  }

  async function handleEmail(e) {
    e.preventDefault()
    setEmailStatus('saving'); setEmailError('')
    try {
      const token = await getToken()
      await axios.post('/api/user/change-email',
        { newEmail: newEmail.trim(), currentPassword: emailPassword },
        { headers: { Authorization: `Bearer ${token}` } })
      setEmailPassword('')
      setEmailStatus('sent')
    } catch (err) {
      setEmailError(err.response?.data?.error ?? 'Could not change email.')
      setEmailStatus('idle')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SettingSection title="Account info">
        <InfoRow label="Username" value={`@${currentUser?.username}`} />
        <InfoRow label="Email"    value={user?.email} />
      </SettingSection>

      <SettingSection title="Change username">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 12px' }}>Letters, numbers, and underscores only (2–30 characters).</p>
        {nextChangeDate && nextChangeDate > new Date() && (
          <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 12px' }}>You can change your username again on {nextChangeDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.</p>
        )}
        {usernameStatus === 'saved' ? (
          <div className="settings-success">Username updated successfully.</div>
        ) : (
          <form style={{ display: 'flex', flexDirection: 'column', gap: 12 }} onSubmit={handleUsername}>
            <input
              type="text"
              className="auth-input"
              value={newUsername}
              onChange={(e) => { setNewUsername(e.target.value); setUsernameError('') }}
              placeholder={`Current: @${currentUser?.username}`}
              required
              disabled={nextChangeDate && nextChangeDate > new Date()}
            />
            {usernameError && <div className="auth-error">{usernameError}</div>}
            <div>
              <button type="submit" className="btn btn-primary" disabled={usernameStatus === 'saving' || (nextChangeDate && nextChangeDate > new Date())} style={{ fontSize: '0.875rem' }}>
                {usernameStatus === 'saving' ? 'Updating…' : 'Update username'}
              </button>
            </div>
          </form>
        )}
      </SettingSection>

      <SettingSection title="Change email">
        {!hasEmailIdentity ? (
          <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>Your email is managed by your Google/Discord sign-in.</p>
        ) : (
        <>
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 12px' }}>A confirmation link will be sent to your new address.</p>
        {emailStatus === 'sent' ? (
          <div className="settings-success">Check your new inbox for a confirmation link.</div>
        ) : (
          <form style={{ display: 'flex', flexDirection: 'column', gap: 12 }} onSubmit={handleEmail}>
            {/* Hidden username anchor — lets password managers identify which
                account the current-password field below belongs to. Matches the
                change-password block's pattern. */}
            {hasEmailIdentity && (
              <input
                type="text"
                autoComplete="username"
                value={user?.email ?? ''}
                readOnly
                style={{ display: 'none' }}
                tabIndex={-1}
                aria-hidden="true"
              />
            )}
            <input
              type="email"
              className="auth-input"
              value={newEmail}
              onChange={(e) => { setNewEmail(e.target.value); setEmailError('') }}
              placeholder="new@example.com"
              required
            />
            {hasEmailIdentity && (
              <input
                type="password"
                className="auth-input"
                value={emailPassword}
                onChange={(e) => { setEmailPassword(e.target.value); setEmailError('') }}
                placeholder="Current password"
                name="current-password"
                autoComplete="current-password"
                required
              />
            )}
            {emailError && <div className="auth-error">{emailError}</div>}
            <div>
              <button type="submit" className="btn btn-primary" disabled={emailStatus === 'saving'} style={{ fontSize: '0.875rem' }}>
                {emailStatus === 'saving' ? 'Sending…' : 'Update email'}
              </button>
            </div>
          </form>
        )}
        </>
        )}
      </SettingSection>

      <SettingSection title="Change password">
        <form style={{ display: 'flex', flexDirection: 'column', gap: 12 }} onSubmit={handlePassword}>
          {/* Hidden username anchor — lets password managers identify which
              account this password belongs to. Without it, browsers fall back
              to heuristics that put suggested passwords in the wrong field. */}
          <input
            type="text"
            autoComplete="username"
            value={user?.email ?? ''}
            readOnly
            style={{ display: 'none' }}
            tabIndex={-1}
            aria-hidden="true"
          />
          <input
            type="password"
            className="auth-input"
            value={pwForm.current}
            onChange={(e) => { setPwForm((f) => ({ ...f, current: e.target.value })); setPwError('') }}
            placeholder="Current password"
            name="current-password"
            autoComplete="current-password"
            required
          />
          <input
            type="password"
            className="auth-input"
            value={pwForm.next}
            onChange={(e) => { setPwForm((f) => ({ ...f, next: e.target.value })); setPwError('') }}
            placeholder="New password (min 8 characters)"
            minLength={8}
            name="new-password"
            autoComplete="new-password"
            required
          />
          <p style={{ fontSize: 13, color: C.textSecondary, margin: '-4px 0 0' }}>
            At least 8 characters. Lowercase, uppercase, digits, and symbols recommended.
          </p>
          <input
            type="password"
            className="auth-input"
            value={pwForm.confirm}
            onChange={(e) => { setPwForm((f) => ({ ...f, confirm: e.target.value })); setPwError('') }}
            placeholder="Confirm new password"
            name="confirm-password"
            autoComplete="new-password"
            required
          />
          {pwError   && <div className="auth-error">{pwError}</div>}
          <div>
            <button type="submit" className="btn btn-primary" disabled={pwStatus === 'saving'} style={{ fontSize: '0.875rem' }}>
              {pwStatus === 'saving' ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </SettingSection>
    </div>
  )
}

// ─── Notifications tab ────────────────────────────────────────────────────────

const BROWSER_NOTIF_KEY = 'verse_browser_notif'

function loadBrowserPrefs() {
  try { return JSON.parse(localStorage.getItem(BROWSER_NOTIF_KEY) ?? '{}') } catch (err) { console.error('[SettingsPage] Failed to parse browser notification prefs:', err); return {} }
}

function saveBrowserPrefs(prefs) {
  localStorage.setItem(BROWSER_NOTIF_KEY, JSON.stringify(prefs))
}

const NOTIF_PREF_ITEMS = [
  { key: 'comment_on_story', label: 'Comments on your stories',     desc: 'When someone comments on a story you published' },
  { key: 'comment_on_quiz',  label: 'Comments on your quizzes',     desc: 'When someone comments on a quiz you published' },
  { key: 'reply_to_comment', label: 'Replies to your comments',     desc: 'When someone replies to one of your comments' },
  { key: 'story_updated',    label: 'Bookmarked story updates',     desc: 'When a story you bookmarked gets a new chapter' },
  { key: 'group_post',       label: 'New group posts',              desc: 'When someone posts in a group you belong to' },
  { key: 'tip_received',     label: 'Tips received',                desc: 'When someone tips one of your stories' },
  { key: 'new_follower',     label: 'New followers',                desc: 'When someone follows your profile' },
  { key: 'like_story',       label: 'Story likes',                 desc: 'When someone likes one of your stories' },
  { key: 'like_quiz',        label: 'Quiz likes',                  desc: 'When someone likes one of your quizzes' },
  { key: 'like_comment',     label: 'Comment likes',               desc: 'When someone likes one of your comments' },
  { key: 'author_announcement', label: 'Author announcements',   desc: 'When an author you follow posts an announcement' },
  { key: 'wall_comment',       label: 'Wall comments',            desc: 'When someone comments on your wall post' },
  { key: 'wall_comment_reply', label: 'Wall comment replies',     desc: 'When someone replies to your wall comment' },
  { key: 'like_wall_post',     label: 'Wall post likes',          desc: 'When someone likes your wall post' },
  { key: 'like_wall_comment',  label: 'Wall comment likes',       desc: 'When someone likes your wall comment' },
  { key: 'follow_request',          label: 'Follow requests',          desc: 'When someone requests to follow your protected profile' },
  { key: 'follow_request_accepted', label: 'Follow request accepted',  desc: 'When someone accepts your follow request' },
]

function NotificationsSection() {
  const { getToken } = useAuth()
  const [subTab, setSubTab] = useState('Preferences')

  // Notification preferences (server-side)
  const [notifPrefs, setNotifPrefs]     = useState({})
  const [prefsLoaded, setPrefsLoaded]   = useState(false)
  const [prefsSaving, setPrefsSaving]   = useState(false)
  const [prefsSaved, setPrefsSaved]     = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const token = await getToken()
        const { data } = await axios.get('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
        setNotifPrefs(data.notificationPrefs ?? {})
      } catch (err) { console.error('[SettingsPage] Failed to load notification prefs:', err) }
      setPrefsLoaded(true)
    }
    load()
  }, [getToken])

  async function saveNotifPrefs(updated) {
    setNotifPrefs(updated)
    setPrefsSaved(false)
    setPrefsSaving(true)
    try {
      const token = await getToken()
      await axios.patch('/api/profile', { notification_prefs: updated }, { headers: { Authorization: `Bearer ${token}` } })
      setPrefsSaved(true)
      setTimeout(() => setPrefsSaved(false), 3000)
    } catch (err) { console.error('[SettingsPage] Failed to save notification prefs:', err) }
    setPrefsSaving(false)
  }

  const [emailFreq, setEmailFreq]     = useState('daily')
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailSaved, setEmailSaved]   = useState(false)

  // Load email frequency from the same profile fetch
  useEffect(() => {
    async function loadEmail() {
      try {
        const token = await getToken()
        const { data } = await axios.get('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
        setEmailFreq(data.emailFrequency ?? 'daily')
      } catch (err) { console.error('[SettingsPage] Failed to load email frequency pref:', err) }
    }
    loadEmail()
  }, [getToken])

  async function saveEmailPref(key, value) {
    setEmailSaving(true)
    setEmailSaved(false)
    try {
      const token = await getToken()
      if (key === 'frequency') {
        setEmailFreq(value)
        await axios.patch('/api/profile', { email_frequency: value }, { headers: { Authorization: `Bearer ${token}` } })
      } else {
        const updated = { ...notifPrefs, [key]: value }
        setNotifPrefs(updated)
        await axios.patch('/api/profile', { notification_prefs: updated }, { headers: { Authorization: `Bearer ${token}` } })
      }
      setEmailSaved(true)
      setTimeout(() => setEmailSaved(false), 3000)
    } catch (err) { console.error('[SettingsPage] Failed to save email pref:', err) }
    setEmailSaving(false)
  }

  // Browser notification state
  const supported = typeof window !== 'undefined' && 'Notification' in window
  const [permission, setPermission] = useState(supported ? Notification.permission : 'denied')
  const [browserPrefs, setBrowserPrefs] = useState(loadBrowserPrefs)

  async function handleBrowserToggle(key) {
    const next = !browserPrefs[key]

    // If turning on, request permission first
    if (next && permission !== 'granted') {
      const result = await Notification.requestPermission()
      setPermission(result)
      if (result !== 'granted') return
    }

    const updated = { ...browserPrefs, [key]: next }
    setBrowserPrefs(updated)
    saveBrowserPrefs(updated)
  }

  const permissionLabel = {
    granted: { text: 'Notifications allowed', color: C.textSuccess },
    denied:  { text: 'Notifications blocked — check your browser settings', color: C.textDanger },
    default: { text: 'Permission not yet granted', color: C.textTertiary },
  }[permission] ?? { text: '', color: C.textTertiary }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Inner sub-tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}` }}>
        {['Preferences', 'Email', 'Browser'].map((t) => (
          <button
            key={t}
            onClick={() => setSubTab(t)}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: subTab === t ? 600 : 400,
              color: subTab === t ? C.textPrimary : C.textSecondary,
              background: 'none',
              border: 'none',
              borderBottom: subTab === t ? '2px solid var(--purple)' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
              fontFamily: 'inherit',
              transition: 'color 0.15s',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {subTab === 'Preferences' && (
        <SettingSection title="Notification preferences">
          <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 4px' }}>Choose which in-app notifications you receive.</p>
          {!prefsLoaded ? (
            <p style={{ fontSize: 13, color: C.textTertiary }}>Loading…</p>
          ) : (
            <>
              {NOTIF_PREF_ITEMS.map((item) => (
                <ToggleRow
                  key={item.key}
                  label={item.label}
                  desc={item.desc}
                  checked={notifPrefs[item.key] !== false}
                  onChange={() => saveNotifPrefs({ ...notifPrefs, [item.key]: notifPrefs[item.key] === false })}
                />
              ))}
              {prefsSaving && <p style={{ fontSize: 12, color: C.textTertiary, margin: '4px 0 0' }}>Saving…</p>}
              {prefsSaved && <p style={{ fontSize: 12, color: C.textSuccess, margin: '4px 0 0' }}>Saved!</p>}
            </>
          )}
        </SettingSection>
      )}

      {subTab === 'Email' && (
        <SettingSection title="Email notifications">
          <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 4px' }}>Emails are sent from noreply@versesite.net.</p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>Daily email digest</div>
              <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>Receive one summary email per day at 10 AM ET covering all your notifications</div>
            </div>
            <button
              className={`toggle-switch${emailFreq !== 'off' ? ' toggle-switch--on' : ''}`}
              onClick={() => saveEmailPref('frequency', emailFreq === 'off' ? 'daily' : 'off')}
              aria-pressed={emailFreq !== 'off'}
            >
              <div className={`toggle-switch__thumb${emailFreq !== 'off' ? ' toggle-switch__thumb--on' : ''}`} />
            </button>
          </div>
          {emailSaving && <p style={{ fontSize: 12, color: C.textTertiary, margin: '4px 0 0' }}>Saving…</p>}
          {emailSaved && <p style={{ fontSize: 12, color: C.textSuccess, margin: '4px 0 0' }}>Saved!</p>}
        </SettingSection>
      )}

      {subTab === 'Browser' && (
        <SettingSection title="Browser / app notifications">
          {!supported ? (
            <p style={{ fontSize: 13, color: C.textTertiary }}>Your browser does not support push notifications.</p>
          ) : (
            <>
              <p style={{ fontSize: 13, color: permissionLabel.color, margin: '0 0 4px' }}>
                {permissionLabel.text}
              </p>
              {[
                { key: 'messages',             label: 'Messages',               desc: 'When you receive a new message' },
                { key: 'comments',             label: 'Comments',               desc: 'When someone comments on your story or quiz' },
                { key: 'replies',              label: 'Replies',                desc: 'When someone replies to your comment' },
                { key: 'likes',                label: 'Likes',                  desc: 'When someone likes your story, quiz, or comment' },
                { key: 'new_follower',         label: 'New followers',          desc: 'When someone follows your profile' },
                { key: 'tips',                 label: 'Tips received',          desc: 'When someone tips one of your stories' },
                { key: 'group_posts',          label: 'Group posts',            desc: 'When someone posts in a group you belong to' },
                { key: 'announcements',        label: 'Author announcements',   desc: 'When an author you follow posts an announcement' },
                { key: 'story_updates',        label: 'Story updates',          desc: 'When a bookmarked story gets a new chapter' },
              ].map(item => (
                <ToggleRow
                  key={item.key}
                  label={item.label}
                  desc={item.desc}
                  checked={!!browserPrefs[item.key] && permission === 'granted'}
                  onChange={() => handleBrowserToggle(item.key)}
                  disabled={permission === 'denied'}
                />
              ))}
            </>
          )}
        </SettingSection>
      )}
    </div>
  )
}

// ─── Appearance tab ───────────────────────────────────────────────────────────

function AppearanceSection() {
  const { theme, setTheme, accent, setAccentTheme } = useTheme()
  const { reducedMotion, setReducedMotion } = useReducedMotion()
  const { getToken } = useAuth()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SettingSection title="Theme">
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>Choose your interface theme preference.</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { id: 'light', label: '☀️ Light', desc: 'Default light theme' },
            { id: 'dark', label: '🌙 Dark', desc: 'Easier on the eyes' },
            { id: 'high-contrast', label: '👁️ High Contrast', desc: 'Maximum legibility (WCAG AAA)' }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              style={{
                flex: '1 1 180px',
                padding: '16px',
                borderRadius: 12,
                border: theme === t.id ? '2px solid var(--accent)' : '1px solid var(--border)',
                background: theme === t.id ? 'var(--accent-soft)' : 'var(--surface)',
                color: theme === t.id ? 'var(--accent)' : 'var(--text)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
                fontFamily: 'inherit'
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t.label}</div>
              <div style={{ fontSize: 12, color: theme === t.id ? 'var(--accent)' : 'var(--text-faint)' }}>{t.desc}</div>
            </button>
          ))}
        </div>
      </SettingSection>

      <SettingSection title="Reduced motion">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.borderFaint}` }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>
              {reducedMotion ? 'Reduced motion on' : 'Reduced motion off'}
            </div>
            <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>
              {reducedMotion
                ? 'Animations and transitions are minimized.'
                : 'Keep motion effects enabled.'}
            </div>
          </div>
          <button
            className={`toggle-switch${reducedMotion ? ' toggle-switch--on' : ''}`}
            onClick={() => setReducedMotion(!reducedMotion)}
            aria-pressed={reducedMotion}
          >
            <div className={`toggle-switch__thumb${reducedMotion ? ' toggle-switch__thumb--on' : ''}`} />
          </button>
        </div>
      </SettingSection>

      <SettingSection title="Accent color">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 12px' }}>Choose your accent color. This syncs across all your devices.</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {Object.entries(ACCENT_PRESETS).map(([name, preset]) => (
            <button
              key={name}
              onClick={async () => {
                setAccentTheme(name)
                try {
                  const token = await getToken()
                  await axios.patch('/api/profile', { accent_theme: name }, { headers: { Authorization: `Bearer ${token}` } })
                } catch (err) { console.error('[SettingsPage] Failed to save accent theme:', err) }
              }}
              style={{
                width: 40, height: 40, borderRadius: '50%',
                background: preset.accent, border: accent === name ? '3px solid var(--text)' : '3px solid transparent',
                cursor: 'pointer', position: 'relative',
                boxShadow: accent === name ? `0 0 0 2px ${preset.accent}` : 'none',
                transition: 'all 0.15s',
              }}
              title={name.charAt(0).toUpperCase() + name.slice(1)}
              aria-label={`${name} accent color`}
            >
              {accent === name && (
                <span style={{ color: '#fff', fontSize: 16, fontWeight: 700, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      </SettingSection>
    </div>
  )
}

// ─── Content tab ─────────────────────────────────────────────────────────────

function ContentSection() {
  const { getToken } = useAuth()
  const [hideExplicit, setHideExplicit] = useState(false)
  const [hideMature, setHideMature] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const token = await getToken()
        const { data } = await axios.get('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
        setHideExplicit(data.hideExplicit ?? false)
        setHideMature(data.hideMature ?? false)
      } catch (err) { console.error('[SettingsPage] Failed to load content filter prefs:', err) }
      setLoading(false)
    }
    load()
  }, [getToken])

  async function togglePref(key, current, setter) {
    const next = !current
    setter(next)
    setSaving(true)
    try {
      const token = await getToken()
      await axios.patch('/api/profile', { [key]: next }, { headers: { Authorization: `Bearer ${token}` } })
    } catch {
      setter(current) // revert on failure
    }
    setSaving(false)
  }

  if (loading) return <div style={{ fontSize: 14, color: C.textSecondary }}>Loading…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SettingSection title="Mature content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.borderFaint}` }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>
              Hide mature stories
            </div>
            <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>
              All Mature-rated stories will be hidden from your feed, search, and browse pages.
            </div>
          </div>
          <button
            className={`toggle-switch${hideMature ? ' toggle-switch--on' : ''}`}
            onClick={() => togglePref('hide_mature', hideMature, setHideMature)}
            disabled={saving}
            aria-pressed={hideMature}
          >
            <div className={`toggle-switch__thumb${hideMature ? ' toggle-switch__thumb--on' : ''}`} />
          </button>
        </div>
      </SettingSection>

      <SettingSection title="Explicit content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${C.borderFaint}` }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>
              Hide sexually explicit stories
            </div>
            <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>
              Sexually explicit stories will be hidden from your feed, search, and browse pages.
            </div>
          </div>
          <button
            className={`toggle-switch${hideExplicit ? ' toggle-switch--on' : ''}`}
            onClick={() => togglePref('hide_explicit', hideExplicit, setHideExplicit)}
            disabled={saving}
            aria-pressed={hideExplicit}
          >
            <div className={`toggle-switch__thumb${hideExplicit ? ' toggle-switch__thumb--on' : ''}`} />
          </button>
        </div>
      </SettingSection>
    </div>
  )
}

// ─── Privacy tab ──────────────────────────────────────────────────────────────

function PrivacySection() {
  const { user, profile, signOut, getToken } = useAuth()
  const navigate = useNavigate()
  const liveConsent = useCookieConsent()

  const [messagePrivacy, setMessagePrivacy] = useState('everyone')
  const [followerPrivacy, setFollowerPrivacy] = useState('everyone')
  const [followingPrivacy, setFollowingPrivacy] = useState('everyone')
  const [showOnline, setShowOnline] = useState(true)
  const [showWall, setShowWall] = useState(true)
  const [privacyMode, setPrivacyMode] = useState(false)
  const [cooldown, setCooldown] = useState('permanent')
  const [toggleOffModal, setToggleOffModal] = useState(null) // { pendingCount } | null
  useEffect(() => {
    if (!user) return
    getToken().then(token =>
      axios.get('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          setMessagePrivacy(r.data.messagePrivacy ?? 'everyone')
          setFollowerPrivacy(r.data.followerPrivacy ?? 'everyone')
          setFollowingPrivacy(r.data.followingPrivacy ?? 'everyone')
          setShowOnline(r.data.showOnlineStatus ?? true)
          setShowWall(r.data.showWall ?? true)
          setPrivacyMode(r.data.privacyMode ?? false)
          setCooldown(r.data.followRequestCooldownAfterReject ?? 'permanent')
        })
    ).catch(err => console.error('[SettingsPage] Failed to fetch privacy settings:', err))
  }, [user])

  async function updatePrivacy(field, value, setter) {
    setter(value)
    try {
      const token = await getToken()
      await axios.patch('/api/profile', { [field]: value }, { headers: { Authorization: `Bearer ${token}` } })
    } catch (err) {
      console.error(`[settings] ${field} update failed:`, err)
    }
  }

  async function togglePrivacyMode() {
    const next = !privacyMode
    // true → false with pending requests outstanding surfaces a confirm
    // modal first; server-side conversion RPC runs inside PATCH /profile
    // when the transition lands (see profile.js).
    if (privacyMode && !next) {
      try {
        const token = await getToken()
        const { data: pending } = await axios.get('/api/follow-requests/inbox', {
          headers: { Authorization: `Bearer ${token}` },
        })
        const count = (pending ?? []).length
        if (count > 0) {
          setToggleOffModal({ pendingCount: count })
          return
        }
      } catch (err) {
        console.error('[SettingsPage] inbox fetch for toggle-off modal failed:', err)
      }
    }
    updatePrivacy('privacy_mode', next, setPrivacyMode)
  }

  function confirmToggleOff() {
    setToggleOffModal(null)
    updatePrivacy('privacy_mode', false, setPrivacyMode)
  }

  const [cookiePrefs,  setCookiePrefs]  = useState({ analytics: liveConsent.analytics })
  const [cookieStatus, setCookieStatus] = useState('idle')

  function saveCookies() {
    setCookieStatus('saving')
    const next = { necessary: true, ...cookiePrefs, decided: true }
    localStorage.setItem(COOKIE_KEY, JSON.stringify(next))
    window.dispatchEvent(new StorageEvent('storage', { key: COOKIE_KEY, newValue: JSON.stringify(next) }))
    setTimeout(() => setCookieStatus('saved'), 600)
  }

  const [exportStatus, setExportStatus] = useState('idle')
  async function handleExport() {
    setExportStatus('loading')
    try {
      const token = await getToken()
      // 55s < the 60s Vercel maxDuration (vercel.json) so a heavy export fails
      // with a controlled client timeout instead of hanging for the raw 504.
      // Per-call only — does not change the bare axios singleton's default.
      const res  = await axios.get('/api/user/export', { headers: { Authorization: `Bearer ${token}` }, timeout: 55000 })
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href = url; a.download = 'verse-data-export.json'; a.click()
      URL.revokeObjectURL(url)
      setExportStatus('idle')
    } catch (err) { console.error('[SettingsPage] Failed to export user data:', err); setExportStatus('error') }
  }

  const [storyExportStatus, setStoryExportStatus] = useState('idle')
  async function handleStoryExport() {
    setStoryExportStatus('loading')
    try {
      const token = await getToken()
      const res = await axios.get('/api/user/export-stories', { headers: { Authorization: `Bearer ${token}` } })
      const { stories } = res.data

      let text = ''
      for (const story of stories) {
        text += `${'='.repeat(60)}\n`
        text += `${story.title}\n`
        text += `by ${story.author}\n`
        text += `${'='.repeat(60)}\n\n`
        text += `Fandom: ${story.fandom}\n`
        if (story.genre?.length) text += `Genre: ${story.genre.join(', ')}\n`
        if (story.tags?.length) text += `Tags: ${story.tags.join(', ')}\n`
        text += `Rating: ${story.rating} | Status: ${story.status} | Words: ${(story.words ?? 0).toLocaleString()}\n`
        if (story.description) text += `\nDescription:\n${story.description}\n`
        text += '\n'

        for (const ch of story.chapters) {
          text += `${'-'.repeat(40)}\n`
          text += `Chapter ${ch.number}: ${ch.title}\n`
          text += `${'-'.repeat(40)}\n\n`
          text += (ch.content ?? '').replace(/<[^>]+>/g, '') + '\n\n'
        }
        text += '\n\n'
      }

      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = 'verse-my-stories.txt'; a.click()
      URL.revokeObjectURL(url)
      setStoryExportStatus('idle')
    } catch (err) { console.error('[SettingsPage] Failed to export stories:', err); setStoryExportStatus('error') }
  }

  const [deleteModal,    setDeleteModal]    = useState(false)
  const [deleteInput,    setDeleteInput]    = useState('')
  const [deleteStatus,   setDeleteStatus]   = useState('idle')
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteError,    setDeleteError]    = useState('')
  const [hasEmailIdentity, setHasEmailIdentity] = useState(null)
  useEffect(() => {
    if (!deleteModal) return
    supabase.auth.getUser().then(({ data }) => {
      setHasEmailIdentity((data?.user?.identities ?? []).some(i => i.provider === 'email'))
    })
  }, [deleteModal])
  const isOAuthUser = hasEmailIdentity === false
  async function handleDelete() {
    if (deleteInput.trim() !== 'DELETE') return
    if (!isOAuthUser && !deletePassword) return
    setDeleteStatus('loading')
    setDeleteError('')
    try {
      const token = await getToken()
      await axios.delete('/api/user/account', {
        headers: { Authorization: `Bearer ${token}` },
        data: isOAuthUser ? { confirmation: 'DELETE' } : { password: deletePassword },
      })
      await signOut()
      navigate('/')
    } catch (err) {
      console.error('[SettingsPage] Failed to delete account:', err)
      setDeleteError(err.response?.data?.error ?? 'Failed to delete account. Please try again.')
      setDeleteStatus('error')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SettingSection title="Cookies">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 4px' }}>
          Verse only uses essential cookies to keep you logged in. No tracking, no ads, no third-party cookies.
          {' '}<Link to="/cookie-policy" style={{ color: 'var(--purple)' }}>Learn more</Link>
        </p>
      </SettingSection>

      {!isTeenAccount(profile) && (
        <SettingSection title="Messages">
          <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 8px' }}>Control who can send you direct messages.</p>
          <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>Who can message you</label>
          <select
            className="settings-select"
            value={messagePrivacy}
            onChange={e => updatePrivacy('message_privacy', e.target.value, setMessagePrivacy)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.875rem' }}
          >
            <option value="everyone">Everyone</option>
            <option value="following">People I follow</option>
            <option value="nobody">Nobody</option>
          </select>
        </SettingSection>
      )}

      <SettingSection title="Profile visibility">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 8px' }}>Control who can see your follower and following lists. When set to private, counts are also hidden from others.</p>
        <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>Who can see your followers</label>
        <select
          className="settings-select"
          value={followerPrivacy}
          onChange={e => updatePrivacy('follower_privacy', e.target.value, setFollowerPrivacy)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.875rem', marginBottom: 12 }}
        >
          <option value="everyone">Everyone</option>
          <option value="following">People I follow</option>
          <option value="nobody">Nobody</option>
        </select>
        <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>Who can see who you follow</label>
        <select
          className="settings-select"
          value={followingPrivacy}
          onChange={e => updatePrivacy('following_privacy', e.target.value, setFollowingPrivacy)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.875rem' }}
        >
          <option value="everyone">Everyone</option>
          <option value="following">People I follow</option>
          <option value="nobody">Nobody</option>
        </select>
      </SettingSection>

      <SettingSection title="Online status">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 8px' }}>Control whether other users can see when you're active on Verse.</p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={showOnline}
            onChange={() => updatePrivacy('show_online_status', !showOnline, setShowOnline)}
          />
          Show when I'm online
        </label>
      </SettingSection>

      <SettingSection title="Wall visibility">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 8px' }}>Control whether your wall tab is visible to other users on your profile.</p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={showWall}
            onChange={() => updatePrivacy('show_wall', !showWall, setShowWall)}
          />
          Show wall on profile
        </label>
      </SettingSection>

      <SettingSection title="Follow approval">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 8px' }}>When enabled, new followers send a request you can accept or decline. Existing followers stay.</p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={privacyMode}
            onChange={togglePrivacyMode}
          />
          Require approval to follow
        </label>
        {privacyMode && (
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>After declining a request, lock that person out for</label>
            <select
              className="settings-select"
              value={cooldown}
              onChange={e => updatePrivacy('follow_request_cooldown_after_reject', e.target.value, setCooldown)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.875rem' }}
            >
              <option value="24h">24 hours</option>
              <option value="7d">7 days</option>
              <option value="permanent">Permanently (default)</option>
            </select>
          </div>
        )}
      </SettingSection>

      <SettingSection title="Export your stories">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 12px' }}>Download all your published stories as a plain-text file, including every chapter.</p>
        {storyExportStatus === 'error' && <div className="auth-error" style={{ marginBottom: 10 }}>Export failed. Please try again.</div>}
        <button className="btn btn-outline" onClick={handleStoryExport} disabled={storyExportStatus === 'loading' || !user} style={{ fontSize: '0.875rem' }}>
          {storyExportStatus === 'loading' ? 'Preparing…' : 'Export my stories'}
        </button>
      </SettingSection>

      <SettingSection title="Download your data">
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 12px' }}>Get a JSON file of your profile, stories, and activity.</p>
        {exportStatus === 'error' && <div className="auth-error" style={{ marginBottom: 10 }}>Export failed. Please try again.</div>}
        <button className="btn btn-outline" onClick={handleExport} disabled={exportStatus === 'loading' || !user} style={{ fontSize: '0.875rem' }}>
          {exportStatus === 'loading' ? 'Preparing…' : 'Download my data'}
        </button>
      </SettingSection>

      <SettingSection title="Delete account" danger>
        <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 12px' }}>Hides your account and content immediately. After 180 days your account and all associated data are permanently deleted. You can restore your account within the window by signing back in.</p>
        <button className="settings-danger-btn" onClick={() => {
          setDeleteModal(true)
          setDeleteInput('')
          setDeletePassword('')
          setDeleteError('')
          setDeleteStatus('idle')
        }} disabled={!user}>
          Delete my account
        </button>
      </SettingSection>

      <SettingSection title="Your privacy rights">
        <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.7 }}>
          <p style={{ margin: '0 0 8px' }}><strong>EU/EEA residents (GDPR):</strong> You have the right to access, correct, delete, and export your personal data. You can also object to processing and withdraw your consent at any time.</p>
          <p style={{ margin: '0 0 8px' }}><strong>California residents (CCPA):</strong> You have the right to know what data we collect, delete it, and opt out of any sale (we don't sell your data).</p>
          <p style={{ margin: 0 }}>To exercise any of these rights, email <a href="mailto:support@versesite.com" style={{ color: 'var(--purple)' }}>support@versesite.com</a>. We'll respond within 30 days.</p>
        </div>
      </SettingSection>

      {toggleOffModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setToggleOffModal(null) }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Turn off approval-required mode?</h2>
            <p className="modal-desc">
              You have <strong>{toggleOffModal.pendingCount}</strong> pending follow request{toggleOffModal.pendingCount === 1 ? '' : 's'}.
              Turning off approval-required mode will accept all of them as followers.
            </p>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setToggleOffModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmToggleOff}>Turn off &amp; accept all</button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setDeleteModal(false)
            setDeleteInput('')
            setDeletePassword('')
            setDeleteError('')
            setDeleteStatus('idle')
          }
        }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Delete your account?</h2>
            <p className="modal-desc">Your account will be hidden immediately. Your profile, stories, and quizzes will no longer be visible to anyone. After 180 days, your account and all associated data are permanently deleted and cannot be recovered. Any Verse coins on your account are forfeit immediately and are not restored if you change your mind. You can restore your account anytime within 180 days by signing back in.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '16px 0' }}>
              <div className="auth-field">
                <label className="auth-label" htmlFor="delete-confirm">Type DELETE to confirm</label>
                <input
                  id="delete-confirm"
                  type="text"
                  className="auth-input"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                />
              </div>
              {hasEmailIdentity === true && (
                <div className="auth-field">
                  <label className="auth-label" htmlFor="delete-password">Confirm your password</label>
                  <input
                    id="delete-password"
                    type="password"
                    className="auth-input"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
              )}
            </div>
            {deleteStatus === 'error' && <div className="auth-error" style={{ marginBottom: 10 }}>{deleteError || 'Deletion failed. Try again or contact support.'}</div>}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => { setDeleteModal(false); setDeleteInput(''); setDeletePassword(''); setDeleteError(''); setDeleteStatus('idle') }}>Cancel</button>
              <button className="settings-danger-btn" onClick={handleDelete} disabled={deleteInput.trim() !== 'DELETE' || hasEmailIdentity === null || (hasEmailIdentity === true && !deletePassword) || deleteStatus === 'loading'}>
                {deleteStatus === 'loading' ? 'Deleting…' : 'Yes, delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Blocked users tab ───────────────────────────────────────────────────────

function BlockedUsersTab() {
  const { user, getToken } = useAuth()
  const [blockedUsers, setBlockedUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [unblocking, setUnblocking] = useState(null)

  useEffect(() => {
    if (!user) { setLoading(false); return }
    ;(async () => {
      try {
        const token = await getToken()
        const res = await axios.get('/api/blocks/list', { headers: { Authorization: `Bearer ${token}` } })
        setBlockedUsers(res.data)
      } catch (err) { console.error('[SettingsPage] Failed to load blocked users:', err) }
      setLoading(false)
    })()
  }, [user?.id])

  async function handleUnblock(username) {
    setUnblocking(username)
    try {
      const token = await getToken()
      await axios.delete(`/api/blocks/${username}`, { headers: { Authorization: `Bearer ${token}` } })
      setBlockedUsers(prev => prev.filter(u => u.username !== username))
    } catch (err) { console.error('[SettingsPage] Failed to unblock user:', err) }
    setUnblocking(null)
  }

  return (
    <SettingSection title="Blocked users">
      {loading ? (
        <p style={{ fontSize: 13, color: C.textSecondary }}>Loading…</p>
      ) : blockedUsers.length === 0 ? (
        <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>You haven't blocked anyone.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {blockedUsers.map(u => (
            <div key={u.userId} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Link to={`/user/${u.username}`} style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: u.avatarUrl ? undefined : (u.avatarGrad || 'linear-gradient(135deg, #7C3AED, #EC4899)'),
                overflow: 'hidden', textDecoration: 'none', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
              }}>
                {u.avatarUrl
                  ? <img src={u.avatarUrl} alt={u.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (u.displayName?.[0] ?? '?').toUpperCase()
                }
              </Link>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link to={`/user/${u.username}`} style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', textDecoration: 'none' }}>
                  {u.displayName ?? u.username}
                </Link>
                <div style={{ fontSize: 11, color: C.textSecondary }}>@{u.username}</div>
              </div>
              <button
                className="btn btn-outline"
                style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                onClick={() => handleUnblock(u.username)}
                disabled={unblocking === u.username}
              >
                {unblocking === u.username ? 'Unblocking…' : 'Unblock'}
              </button>
            </div>
          ))}
        </div>
      )}
    </SettingSection>
  )
}

// ─── Payments / KYC section ──────────────────────────────────────────────────

const KYC_DOC_TYPES = [
  { value: 'passport',        label: 'Passport' },
  { value: 'drivers_license', label: "Driver's license" },
  { value: 'national_id',     label: 'National ID card' },
]

const KYC_STATUS_DISPLAY = {
  none:     { label: 'Not verified',        color: C.textSecondary, icon: '○' },
  pending:  { label: 'Verification pending', color: '#f59e0b',       icon: '◔' },
  verified: { label: 'Verified',             color: C.textSuccess,   icon: '✓' },
  rejected: { label: 'Verification rejected', color: C.textDanger,   icon: '✕' },
}

function PaymentsSection() {
  const { getToken } = useAuth()
  const { balance, purchasedBalance, earnedBalance, heldBalance, redeemableBalance, loading: balLoading, refresh: refreshBalance } = useCoinBalance()
  const [searchParams, setSearchParams] = useSearchParams()

  const [kycStatus, setKycStatus]     = useState(null)
  const [kycLoading, setKycLoading]   = useState(true)

  const [packages, setPackages]       = useState([])
  const [pkgLoading, setPkgLoading]   = useState(true)
  const [buying, setBuying]           = useState(null)
  const [purchaseMsg, setPurchaseMsg] = useState(null)

  const [showForm, setShowForm]       = useState(false)
  const [fullName, setFullName]       = useState('')
  const [dob, setDob]                 = useState('')
  const [country, setCountry]         = useState('')
  const [docType, setDocType]         = useState('passport')
  const [docFile, setDocFile]         = useState(null)
  const [uploading, setUploading]     = useState(false)
  const [submitting, setSubmitting]   = useState(false)
  const [formError, setFormError]     = useState(null)
  const [formSuccess, setFormSuccess] = useState(false)

  // Load coin packages
  useEffect(() => {
    axios.get('/api/coins/packages')
      .then(r => setPackages(r.data))
      .catch(() => {})
      .finally(() => setPkgLoading(false))
  }, [])

  // Handle Stripe redirect
  useEffect(() => {
    const status = searchParams.get('purchase')
    if (status === 'success') {
      setPurchaseMsg({ type: 'success', text: 'Payment successful! Your coins will appear shortly.' })
      refreshBalance()
      searchParams.delete('purchase')
      setSearchParams(searchParams, { replace: true })
    } else if (status === 'cancelled') {
      setPurchaseMsg({ type: 'info', text: 'Purchase cancelled.' })
      searchParams.delete('purchase')
      setSearchParams(searchParams, { replace: true })
    }
  }, [])

  async function handleBuy(pkg) {
    setBuying(pkg.id)
    setPurchaseMsg(null)
    try {
      const token = await getToken()
      const idempotencyKey = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)
      const { data } = await axios.post('/api/coins/purchase', { packageId: pkg.id }, {
        headers: { Authorization: `Bearer ${token}`, 'Idempotency-Key': idempotencyKey },
      })
      if (!data.url?.startsWith('https://checkout.stripe.com')) {
        setPurchaseMsg({ type: 'error', text: 'Invalid checkout URL. Please try again.' })
        setBuying(null)
        return
      }
      window.location.href = data.url
    } catch (err) {
      setPurchaseMsg({ type: 'error', text: err.response?.data?.error ?? 'Failed to start checkout.' })
      setBuying(null)
    }
  }

  useEffect(() => {
    async function loadKyc() {
      try {
        const token = await getToken()
        const { data } = await axios.get('/api/coins/kyc/status', {
          headers: { Authorization: `Bearer ${token}` },
        })
        setKycStatus(data)
      } catch (err) { console.error('[SettingsPage] Failed to load KYC status:', err) }
      setKycLoading(false)
    }
    loadKyc()
  }, [getToken])

  async function handleSubmit(e) {
    e.preventDefault()
    setFormError(null)
    if (!docFile) return setFormError('Please select a document file.')

    const ext = docFile.name.split('.').pop().toLowerCase()
    if (!['jpg', 'jpeg', 'png', 'pdf', 'webp'].includes(ext)) {
      return setFormError('Unsupported file type. Use JPG, PNG, PDF, or WebP.')
    }

    setUploading(true)
    let documentPath
    try {
      const token = await getToken()

      // Step 1: Get signed upload URL
      const { data: uploadData } = await axios.post('/api/coins/kyc/upload-url', { ext }, {
        headers: { Authorization: `Bearer ${token}` },
      })

      // Step 2: Upload file directly to storage
      await fetch(uploadData.signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': docFile.type || 'application/octet-stream' },
        body: docFile,
      }).then(r => { if (!r.ok) throw new Error('Upload failed') })

      documentPath = uploadData.path
    } catch (err) {
      setFormError(err.response?.data?.error ?? 'Document upload failed. Please try again.')
      setUploading(false)
      return
    }
    setUploading(false)

    // Step 3: Submit KYC with the uploaded document path
    setSubmitting(true)
    try {
      const token = await getToken()
      const idempotencyKey = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)
      await axios.post('/api/coins/kyc', {
        fullName: fullName.trim(),
        dateOfBirth: dob,
        country: country.trim(),
        documentType: docType,
        documentPath,
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Idempotency-Key': idempotencyKey },
      })
      setFormSuccess(true)
      setKycStatus({ status: 'pending' })
      setShowForm(false)
    } catch (err) {
      setFormError(err.response?.data?.error ?? 'Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const status = kycStatus?.status ?? 'none'
  const statusDisplay = KYC_STATUS_DISPLAY[status] ?? KYC_STATUS_DISPLAY.none

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SettingSection title="Coin balance">
        {balLoading ? (
          <p style={{ fontSize: 13, color: C.textTertiary }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <InfoRow label="Total balance" value={`🪙 ${balance.toLocaleString()}`} />
            <InfoRow label="Purchased coins (tippable)" value={purchasedBalance.toLocaleString()} />
            <InfoRow label="Earned coins (total)" value={earnedBalance.toLocaleString()} />
            {heldBalance > 0 && (
              <InfoRow label="On hold (3-day wait)" value={heldBalance.toLocaleString()} />
            )}
            <InfoRow label="Redeemable now" value={redeemableBalance.toLocaleString()} />
            <Link to="/transactions" style={{ fontSize: 13, color: 'var(--purple)', textDecoration: 'none', marginTop: 8, display: 'inline-block' }}>
              View transaction history →
            </Link>
            <p style={{ fontSize: 12, color: C.textTertiary, margin: '8px 0 0' }}>
              Purchased coins can be used to tip authors. Earned coins (from tips you receive) can be tipped or redeemed for cash minus a 30% platform fee. New tips are held for 3 days before they become redeemable.
            </p>
          </div>
        )}
      </SettingSection>

      <SettingSection title="Buy coins — Coming soon">
        <p style={{ fontSize: 13, color: C.textTertiary, margin: '0 0 12px' }}>
          Coin purchases are coming soon. Stay tuned!
        </p>
        {pkgLoading ? (
          <p style={{ fontSize: 13, color: C.textTertiary }}>Loading…</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, opacity: 0.5, pointerEvents: 'none' }}>
            {packages.map(pkg => (
              <div
                key={pkg.id}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '14px 10px', borderRadius: 10,
                  border: `1px solid ${C.border}`, background: C.bg,
                }}
              >
                <span style={{ fontSize: 18, fontWeight: 700, color: C.textPrimary }}>{pkg.coins.toLocaleString()}</span>
                <span style={{ fontSize: 12, color: C.textTertiary }}>coins</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--purple)', marginTop: 4 }}>
                  ${(pkg.price_cents / 100).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </SettingSection>

      <SettingSection title="Identity verification — Coming soon">
        <p style={{ fontSize: 13, color: C.textTertiary, margin: '0 0 12px' }}>
          Identity verification (KYC) for cashouts is coming soon.
        </p>
        <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
        {kycLoading ? (
          <p style={{ fontSize: 13, color: C.textTertiary }}>Loading…</p>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: `1px solid ${C.borderFaint}` }}>
              <span style={{ fontSize: 16 }}>{statusDisplay.icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: statusDisplay.color }}>{statusDisplay.label}</div>
                {status === 'none' && (
                  <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>
                    Verify your identity to redeem earned coins. You must be at least 18 years old.
                  </div>
                )}
                {status === 'pending' && (
                  <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>
                    Your verification is being reviewed. This usually takes 1–2 business days.
                  </div>
                )}
                {status === 'verified' && (
                  <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>
                    Your identity is verified. You can redeem earned coins.
                  </div>
                )}
                {status === 'rejected' && (
                  <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>
                    Your verification was not approved. You can resubmit with correct documents.
                  </div>
                )}
              </div>
            </div>

            {formSuccess && (
              <p style={{ fontSize: 13, color: C.textSuccess, margin: '8px 0' }}>
                Verification submitted! You'll be notified once it's reviewed.
              </p>
            )}

            {(status === 'none' || status === 'rejected') && !showForm && (
              <button
                className="btn btn-primary"
                style={{ alignSelf: 'flex-start', marginTop: 8, fontSize: '0.85rem', padding: '8px 20px' }}
                onClick={() => { setShowForm(true); setFormSuccess(false) }}
              >
                {status === 'rejected' ? 'Resubmit verification' : 'Verify identity'}
              </button>
            )}

            {showForm && (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 4 }}>Full legal name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    required
                    placeholder="As it appears on your ID"
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.textPrimary, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 4 }}>Date of birth</label>
                  <input
                    type="date"
                    value={dob}
                    onChange={e => setDob(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.textPrimary, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 4 }}>Country of residence</label>
                  <input
                    type="text"
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                    required
                    placeholder="e.g. United States"
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.textPrimary, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 4 }}>ID document type</label>
                  <select
                    value={docType}
                    onChange={e => setDocType(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.textPrimary, fontFamily: 'inherit', boxSizing: 'border-box', cursor: 'pointer' }}
                  >
                    {KYC_DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary, display: 'block', marginBottom: 4 }}>Document photo</label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                    onChange={e => setDocFile(e.target.files?.[0] ?? null)}
                    required
                    style={{ width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.textPrimary, fontFamily: 'inherit', boxSizing: 'border-box' }}
                  />
                  <p style={{ fontSize: 11, color: C.textTertiary, margin: '4px 0 0' }}>
                    Accepted: JPG, PNG, PDF, or WebP. Your document is uploaded securely and only accessible to admins.
                  </p>
                </div>

                {formError && <p style={{ fontSize: 13, color: C.textDanger, margin: 0 }}>{formError}</p>}

                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={uploading || submitting || !fullName.trim() || !dob || !country.trim() || !docFile}
                    style={{ fontSize: '0.85rem', padding: '8px 20px' }}
                  >
                    {uploading ? 'Uploading document…' : submitting ? 'Submitting…' : 'Submit verification'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowForm(false)}
                    style={{ fontSize: '0.85rem', padding: '8px 20px' }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        )}
        </div>
      </SettingSection>

      <SettingSection title="Redeem coins — Coming soon">
        <p style={{ fontSize: 13, color: C.textTertiary, margin: 0 }}>
          {status === 'verified'
            ? 'You can redeem earned coins once you have at least 5,000. A 30% platform fee applies — e.g. redeeming 5,000 coins pays out 3,500 coins worth.'
            : 'Verify your identity above to unlock coin redemption.'}
        </p>
        {status === 'verified' && (
          <p style={{ fontSize: 12, color: C.textTertiary, margin: '4px 0 0' }}>
            Stripe Connect integration coming soon — you'll be able to link your bank account and redeem directly.
          </p>
        )}
      </SettingSection>
    </div>
  )
}

// ─── Shared layout helpers ────────────────────────────────────────────────────

function SettingSection({ title, children, danger = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{
        fontSize: 14, fontWeight: 600, margin: 0,
        paddingBottom: 12, borderBottom: `1px solid ${C.borderFaint}`,
        color: danger ? C.textDanger : C.textPrimary,
      }}>
        {title}
      </h2>
      {children}
    </div>
  )
}


function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.borderFaint}` }}>
      <span style={{ fontSize: 13, color: C.textSecondary }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: C.textPrimary }}>{value}</span>
    </div>
  )
}

function ToggleRow({ label, desc, checked, onChange, disabled = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.borderFaint}` }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>{label}</div>
        <div style={{ fontSize: 12, color: C.textTertiary, marginTop: 2 }}>{desc}</div>
      </div>
      <button
        className={`toggle-switch${checked ? ' toggle-switch--on' : ''}${disabled ? ' toggle-switch--disabled' : ''}`}
        onClick={!disabled ? onChange : undefined}
        aria-pressed={checked}
        style={disabled ? { cursor: 'default' } : {}}
      >
        <div className={`toggle-switch__thumb${checked ? ' toggle-switch__thumb--on' : ''}`} />
      </button>
    </div>
  )
}
